// ── BACKGROUND UPLOAD RETRY HOOK ──
// Runs once on mount and then every 30 seconds. Looks at the IndexedDB
// upload queue, picks any item ready for retry (per `backoffMs` schedule),
// runs the recovery pipeline. Returns `{ pendingCount, retrying }` so a
// component can render a soft "saving your interview…" indicator.
//
// Designed to be cheap to mount many times — work is gated by the backoff
// schedule, so spinning the hook up on every authenticated page just polls
// the queue without re-uploading the same file twice.

import { useCallback, useEffect, useRef, useState } from 'react';
import { listPending, updatePending, isReadyToRetry } from '../utils/uploadQueue.js';
import { processQueueItem } from '../services/uploadRecovery.js';
import { sessions } from '../services/api.js';

const POLL_MS = 30_000;

export function useUploadRetry({ enabled = true } = {}) {
  const [pendingCount, setPendingCount] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const cancelRef = useRef(false);

  // What counts as "pending" for the user-facing indicator pill: any
  // queue item that's a real in-flight session upload (kind 'session' or
  // 'segment') and not yet completed.
  //
  // 'checkpoint' items are EXCLUDED — they're proactive rolling snapshots
  // saved every 30 seconds during an active recording (so a crash mid-
  // session is recoverable). Counting them as "pending" makes the pill
  // flash on every 30s tick during a normal recording, which is alarming
  // and wrong. The retry hook still processes any checkpoints it finds at
  // app launch (orphan recovery), but they don't drive the UI.
  function userVisiblePending(items) {
    return items.filter(i => !i.completed && i.kind !== 'checkpoint');
  }

  const refreshCount = useCallback(async () => {
    try {
      const items = await listPending();
      setPendingCount(userVisiblePending(items).length);
    } catch {}
  }, []);

  // One pass through the queue. Doesn't run multiple uploads in parallel —
  // each item is awaited in sequence so we don't slam the network.
  const runOnce = useCallback(async () => {
    if (cancelRef.current) return;
    let items;
    try { items = await listPending(); } catch { return; }
    if (cancelRef.current) return;
    setPendingCount(userVisiblePending(items).length);
    if (items.length === 0) return;

    setRetrying(true);
    for (const item of items) {
      if (cancelRef.current) break;
      if (item.completed) continue;
      // Skip rolling 30-second checkpoints in the normal retry loop —
      // they're snapshot backups for an active session, not stuck uploads.
      // Only consider them after they've been stale for 10+ minutes
      // (the recording has clearly stopped without a clean end, so the
      // checkpoint is now the orphan-recovery source).
      if (item.kind === 'checkpoint') {
        const ageMs = item.lastSavedAt
          ? Date.now() - new Date(item.lastSavedAt).getTime()
          : item.createdAt
            ? Date.now() - new Date(item.createdAt).getTime()
            : 0;
        if (ageMs < 10 * 60 * 1000) continue;
      }
      if (!isReadyToRetry(item)) continue;

      // Mark this attempt before running so concurrent ticks don't double-up.
      const nextAttempt = (item.attempts || 0) + 1;
      await updatePending(item.id, {
        attempts: nextAttempt,
        lastTried: new Date().toISOString(),
      });

      try {
        await processQueueItem({ ...item, attempts: nextAttempt });
      } catch (err) {
        console.warn('[UploadRetry] item ' + item.id + ' attempt ' + nextAttempt + ' failed:', err.message);
        // First failure also fires the dev-team email so we know about it.
        // Subsequent retries are silent (backend would otherwise spam us).
        if (!item.notifiedFailure && item.sessionId) {
          try {
            await sessions.reportAudioFailure(item.sessionId, {
              errorMessage: err.message,
              errorStage: 'client-retry',
              fileSizeBytes: item.audioBlob && item.audioBlob.size ? item.audioBlob.size : null,
              userAgent: navigator.userAgent,
              occurredAt: new Date().toISOString(),
            });
          } catch {}
          await updatePending(item.id, { notifiedFailure: true });
        }
      }
    }
    setRetrying(false);
    await refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    if (!enabled) return;
    cancelRef.current = false;
    runOnce();
    const interval = setInterval(runOnce, POLL_MS);
    // Also retry whenever the network comes back online — handy if the
    // captain/storyteller's wifi blipped during the upload.
    function onOnline() { runOnce(); }
    window.addEventListener('online', onOnline);
    return () => {
      cancelRef.current = true;
      clearInterval(interval);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, runOnce]);

  return { pendingCount, retrying, refreshCount };
}
